import { ComponentProps, FC, ReactNode } from 'react';
import { Stack } from '@mui/material';
import { type SvgIconComponent as MuiSvgIconComponent } from '@mui/icons-material';
import { LinkAsButton, Typography } from 'melp-design/components';
import {
  ACTION_COLOR_BY_VARIANT,
  CARD_STYLES_BY_VARIANT,
  ICON_CONTAINER_STYLES_BY_VARIANT,
  Variant,
} from './info-card.styles';

interface Props {
  icon: MuiSvgIconComponent;
  title: ReactNode;
  text: ReactNode;
  variant?: Variant;
  direction?: 'row' | 'column';
  onClick?: () => void;
  action?: Omit<ComponentProps<typeof LinkAsButton>, 'color'>;
}

const ICON_CONTAINER_SIZE = 40;

export const InfoCard: FC<Props> = ({
  icon: Icon,
  title,
  text,
  variant = 'neutral',
  direction = 'column',
  onClick,
  action,
}) => {
  const isClickable = Boolean(onClick && !action);

  return (
    <Stack
      component={isClickable ? 'button' : 'div'}
      type={isClickable ? 'button' : undefined}
      onClick={onClick}
      borderRadius={2}
      p={2}
      gap={2}
      flexDirection={direction}
      alignItems="flex-start"
      sx={{
        ...CARD_STYLES_BY_VARIANT[variant],
        ...(isClickable ? { cursor: 'pointer', textAlign: 'left' } : {}),
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        borderRadius={1}
        height={ICON_CONTAINER_SIZE}
        width={ICON_CONTAINER_SIZE}
        sx={ICON_CONTAINER_STYLES_BY_VARIANT[variant]}
      >
        <Icon style={{ width: '65%', height: '65%' }} />
      </Stack>

      <Stack gap={0.5} flex={1}>
        <Typography variant={direction === 'column' ? 'h2' : 'h3'}>
          {title}
        </Typography>
        <Typography variant="p1" color="textSecondary">
          {text}
        </Typography>
      </Stack>

      {action ? (
        <LinkAsButton {...action} color={ACTION_COLOR_BY_VARIANT[variant]} />
      ) : null}
    </Stack>
  );
};
